import { FC, FocusEventHandler, useRef } from 'react'
import { DateRangeValue, DateTimePicker, TextField } from '@microservices/wiskey-react-components'
import { Grid } from '@mui/material'
import Divider from '@mui/material/Divider'

import { generalFieldStylesByMode } from '@helpers'

import { truncateDateTimeToMinute } from '../../utils'

type RelativeDatePickerProps = {
  onChange: (value: [DateRangeValue, string, string]) => void
  beforeText?: string
  afterText?: string
  inputFormat?: string
  value: [DateRangeValue, string, string]
  disabled?: boolean
  popoverDateShiftMode?: boolean
  placeholder?: string
  isDialogWindow?: boolean
  min?: number
  max?: number
}

export type RelativeDatePickerForm = {
  value: string | null
  before: string
  after: string
}

export type RelativeType = [DateRangeValue, string, string]

const SearchAssistantRelativeDatePicker: FC<RelativeDatePickerProps> = ({
  onChange,
  value,
  beforeText,
  afterText,
  inputFormat,
  disabled = false,
  popoverDateShiftMode,
  placeholder,
  isDialogWindow,
  min = -24,
  max = 48,
}) => {
  const dateRef = useRef<HTMLInputElement>(null)
  const beforeRef = useRef<HTMLInputElement>(null)
  const afterRef = useRef<HTMLInputElement>(null)

  const handleChange = (value: string) => {
    onChange?.([
      truncateDateTimeToMinute(value),
      beforeRef.current?.value || '',
      afterRef.current?.value || '',
    ])
  }

  const getRelativeValue = (value: string, initValue: string) => {
    let newValue = initValue
    if (value === '' || /^-?\d*$/.test(value)) {
      if (!isNaN(parseInt(value, 10))) {
        let intValue = parseInt(value, 10)
        if (intValue > max) intValue = max
        if (intValue < min) intValue = min
        newValue = intValue.toString()
      } else {
        newValue = value
      }
    }

    return newValue
  }

  const handleChangeBefore: FocusEventHandler<HTMLInputElement> = ({ target }) => {
    const newValue = getRelativeValue(target.value, value[1])

    onChange?.([
      truncateDateTimeToMinute(value[0]) || null,
      newValue,
      afterRef.current?.value || '',
    ])
  }

  const handleChangeAfter: FocusEventHandler<HTMLInputElement> = ({ target }) => {
    const newValue = getRelativeValue(target.value, value[2])

    onChange?.([
      truncateDateTimeToMinute(value[0]) || null,
      beforeRef.current?.value || '',
      newValue,
    ])
  }

  return (
    <Grid container item flexWrap='nowrap'>
      <Grid item flex={1}>
        <DateTimePicker
          fullWidth
          disabled={disabled}
          inputFormat={inputFormat}
          inputRef={dateRef}
          placeholder={placeholder}
          popoverDateShiftMode={popoverDateShiftMode}
          value={value[0]}
          sx={theme => ({
            // @ts-expect-error TODO(isilanov): fix type for generalFieldStylesByMode
            ...generalFieldStylesByMode(theme, undefined, undefined, undefined, isDialogWindow),
          })}
          onChange={handleChange}
        />
      </Grid>
      <Grid container item alignItems='center' flex={1} flexWrap='nowrap' ml={1}>
        <Grid item width='100%'>
          <TextField
            fullWidth
            disabled={disabled}
            inputRef={beforeRef}
            placeholder={beforeText}
            type='text'
            value={value[1]}
            sx={theme => ({
              // @ts-expect-error TODO(isilanov): fix type for generalFieldStylesByMode
              ...generalFieldStylesByMode(theme, undefined, undefined, undefined, isDialogWindow),
            })}
            onChange={handleChangeBefore}
          />
        </Grid>
      </Grid>
      <Grid container item alignItems='center' flex={1} flexWrap='nowrap' ml={1}>
        <Grid item flex={1} width='100%'>
          <Divider sx={{ width: 10, mr: 1 }} />
        </Grid>
        <Grid item width='100%'>
          <TextField
            fullWidth
            disabled={disabled}
            inputRef={afterRef}
            placeholder={afterText}
            type='text'
            value={value[2]}
            sx={theme => ({
              // @ts-expect-error TODO(isilanov): fix type for generalFieldStylesByMode
              ...generalFieldStylesByMode(theme, undefined, undefined, undefined, isDialogWindow),
            })}
            onChange={handleChangeAfter}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export { SearchAssistantRelativeDatePicker }
